import { FC } from 'react';
import styles from './Projects.module.scss';

type Project = {
  title: string;
  summary: string;
  ownership: string;
  impact: string;
  warning?: string;
  stack: string[];
  url?: string;
  linkLabel?: string;
  badge: string;
};

const projects: Project[] = [
  {
    title: 'Investing',
    badge: '🟢 Live',
    summary: 'Personal crypto portfolio platform for tracking investments, performance and asset allocation.',
    ownership: 'Built entirely from scratch, including architecture, authentication, backend APIs and deployment.',
    impact: 'Features interactive charts, portfolio analytics, secure authentication and responsive dashboards.',
    warning:
      'Heads up: hosted on Render free tier. The first request may take around 45 seconds while the server wakes up.',
    stack: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Chart.js'],
    url: 'https://investing-app.com/',
    linkLabel: 'View Project',
  },
  {
    title: 'Prompt Rockets',
    badge: '🟢 Live',
    summary: 'AI-powered SaaS that creates personalized prompts based on user goals and inputs.',
    ownership: 'Designed and developed independently, from concept to production-ready subscription platform.',
    impact: 'Integrated authentication, Stripe subscriptions, backend APIs and AI-powered workflows.',
    warning:
      'Heads up: hosted on Render free tier. The first request may take around 45 seconds while the server wakes up.',
    stack: ['React', 'TypeScript', 'Node.js', 'Stripe', 'Express'],
    url: 'https://promptrockets.com/',
    linkLabel: 'View Project',
  },
  {
    title: 'Ser Mãe Presente',
    badge: '💼 Client Project',
    summary: 'Subscription platform helping parents access educational resources, routines and community content.',
    ownership: 'Complete product delivered for a real client, from UX and development to deployment.',
    impact:
      'Includes authentication, Stripe payments, subscriptions, content management and private community features.',
    stack: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Stripe'],
    url: 'https://sermaepresente.com/',
    linkLabel: 'Visit Website',
  },
  {
    title: 'Crypto Volume Radar',
    badge: '🚧 Beta',
    summary: 'Telegram bot that continuously monitors Solana and Base for high-volume trading opportunities.',
    ownership: 'Personal automation project focused on blockchain analytics and trading signals.',
    impact:
      'Aggregates multiple APIs, validates liquidity, tracks token performance and delivers automated Telegram alerts and daily reports.',
    stack: ['Node.js', 'Telegram API', 'REST API', 'Cron', 'Cloudflare'],
    url: 'https://t.me/CryptoVolumeRadar',
    linkLabel: 'Visit Website',
  },
];

const Projects: FC = () => {
  return (
    <section id="projects" className={styles.projects}>
      <div className={styles.row}>
        <div className={styles.heading}>
          <h2>Featured Projects</h2>

          <p>
            End-to-end products I've designed, developed and launched independently, from idea and architecture to
            deployment and continuous improvement.
          </p>
        </div>

        <div className={styles.grid}>
          {projects.map(project => (
            <article key={project.title} className={styles.card}>
              <span className={styles.kicker}>{project.badge}</span>

              <h3>{project.title}</h3>

              <p>{project.summary}</p>

              <div className={styles.box}>
                <strong>Built</strong>
                <p>{project.ownership}</p>
              </div>

              <div className={styles.box}>
                <strong>Highlights</strong>
                <p>{project.impact}</p>
              </div>

              {project.warning && (
                <div className={styles.box}>
                  <strong>Heads up</strong>
                  <p>{project.warning}</p>
                </div>
              )}

              <ul className={styles.stack}>
                {project.stack.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              {project.url && (
                <a href={project.url} target="_blank" rel="noreferrer" className={styles.link}>
                  {project.linkLabel} →
                </a>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
